// SessionForm.jsx - Reusable session logging form component
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { flushSync } from "react-dom";
import { authService } from "../../services/auth";
import { API_BASE } from "../../config/api";
import { httpRequest } from "../../utils/httpClient";
import WinnerSelector from "./WinnerSelector";
import AddGameModal from "../features/AddGameModal";
import GameSearchDropdown from "./GameSearchDropdown";
import SessionInviteShareModal from "./SessionInviteShareModal";
import { THUMBS_UP_ICON, THUMBS_DOWN_ICON } from "../../constants/icons";
import ParticipantTypeToggle from "./ParticipantTypeToggle";
import logger from '../../utils/logger';

// Helper to detect co-op games from mechanisms/categories
const checkIsCoopGame = (game) => {
  if (!game) return false;
  const features = [
    ...(game.mechanics || []),
    ...(game.categories || [])
  ].map(f => String(f).toLowerCase());
  return features.some(f =>
    f.includes('co-operative') ||
    f.includes('cooperative') ||
    f.includes('co-op') ||
    f.includes('coop')
  );
};

const STORAGE_PREFIX = "pista_sessionform";

const SessionForm = ({
  user,
  initialGame = null,
  initialSessionData = null,
  sessionId = null,
  onSave,
  onInvitesDone,
  onCancel,
  onDataChange,
  showBanner,
  _userMap,
  _fetchUserInfo,
  viewOnly = false
}) => {
  const isSubmitting = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  const finishSubmitting = useCallback(() => {
    isSubmitting.current = false;
    setIsSaving(false);
  }, []);

  const initialParticipantFromGame = {
    rating: initialGame?.preference ?? "",
    ownership_status: initialGame?.ownership_status ?? "",
    has_played: initialGame?.has_played ?? false
  };

  const defaultCurrentUserParticipant = {
    user_id: user?.id,
    username: user?.username || user?.name || 'You',
    score: "",
    rating: initialParticipantFromGame.rating,
    ownership_status: initialParticipantFromGame.ownership_status,
    has_played: initialParticipantFromGame.has_played,
    color: "", // No default color
    isExisting: true,
    isAutomata: false
  };

  const normalizeParticipant = (p) => ({
    ...p,
    isAutomata: p.isAutomata ?? false
  });

  const ensureCurrentUserInParticipants = (participants) => {
    const normalized = participants.map(normalizeParticipant);
    if (!user?.id) return normalized;
    const hasCurrentUser = normalized.some(p => p.user_id === user.id);
    if (hasCurrentUser) return normalized;
    return [defaultCurrentUserParticipant, ...normalized];
  };

  const initialParticipants = sessionId
    ? (initialSessionData?.participants || [defaultCurrentUserParticipant]).map(normalizeParticipant)
    : ensureCurrentUserInParticipants(initialSessionData?.participants || [defaultCurrentUserParticipant]);

  const [sessionData, setSessionData] = useState({
    game: initialGame,
    date: new Date().toISOString().split('T')[0],
    chosen_at: new Date().toISOString(),
    participants: initialParticipants,
    notes: initialSessionData?.notes || "",
    win_loss: initialSessionData?.win_loss ?? "Loss",
    winners: initialSessionData?.winners || []
  });

  const [newUsername, setNewUsername] = useState("");

  // State for toggling add participant input
  const [showAddParticipantInput, setShowAddParticipantInput] = useState(false);

  // User search state for participant addition
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [userSearchResults, setUserSearchResults] = useState([]);

  // Game search state
  const [gameSearchQuery, setGameSearchQuery] = useState("");
  const latestGameSearchQueryRef = useRef(gameSearchQuery);
  const [showGameDropdown, setShowGameDropdown] = useState(false);
  const [gameSearchResults, setGameSearchResults] = useState([]);
  const [gameSearchLoading, setGameSearchLoading] = useState(false);
  const [isSelectingGame, setIsSelectingGame] = useState(false);
  const skipGameSearchRef = useRef(false);

  const [isCoopGame, setIsCoopGame] = useState(false);

  // Invite flow: tracks which non-user participants the owner wants to invite
  const [pendingInvites, setPendingInvites] = useState({}); // { participantIndex: true }
  // After save: holds invite data to show share modal
  const [activeShareInvite, setActiveShareInvite] = useState(null); // { token, gameName, guestName, inviterName, queue }
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const pendingSessionSubmitRef = useRef(false);

  // Update co-op status when game changes
  useEffect(() => {
    if (sessionData.game) {
      setIsCoopGame(checkIsCoopGame(sessionData.game));
    }
  }, [sessionData.game]);

  // Returns a stable ID for a participant suitable for winner tracking.
  // Registered users use their real user_id; non-users get a negative
  // index-based synthetic ID so they are individually selectable in
  // WinnerSelector without colliding on `undefined`.
  const participantWinnerId = useCallback((p, idx) => {
    return p.user_id !== null && p.user_id !== undefined ? p.user_id : -(idx + 1);
  }, []);

  // Map an array of winner IDs (which may contain synthetic negatives) back
  // to real user_ids for the backend payload.  Non-users who "win" are
  // represented as 0 (system dummy) because they have no account yet.
  const resolveWinnerIds = useCallback((winnerIds, participants) => {
    const resolved = winnerIds.map(wid => {
      if (wid > 0) return wid; // real registered user
      if (wid === 0) return 0; // explicit system dummy
      // Synthetic negative: -(idx+1) → idx = -wid - 1
      const idx = -wid - 1;
      const p = participants[idx];
      return p?.user_id ?? 0; // non-user winner → 0 (no account)
    });
    // Deduplicate and ensure at least [0] when no real winners remain
    const unique = [...new Set(resolved)];
    return unique.length > 0 ? unique : [0];
  }, []);

  // Auto-determine winners based on game state
  const determineWinners = useCallback(() => {
    const participants = sessionData.participants;
    if (!participants || participants.length === 0) {
      return [0]; // System dummy user
    }

    // For co-op games
    if (isCoopGame) {
      if (sessionData.win_loss === "Win") {
        // Include all participants using their stable winner ID
        return participants.map((p, idx) => participantWinnerId(p, idx));
      } else {
        return [0]; // System dummy user for Loss
      }
    }

    // For competitive games with scores
    const participantsWithScores = participants
      .map((p, idx) => ({ ...p, _wid: participantWinnerId(p, idx) }))
      .filter(p => p.score && p.score !== "" && !isNaN(parseFloat(p.score)));

    if (participantsWithScores.length > 0) {
      const maxScore = Math.max(...participantsWithScores.map(p => parseFloat(p.score)));
      const winners = participantsWithScores.filter(p => parseFloat(p.score) === maxScore);
      // Use stable winner IDs — non-users get their synthetic negative ID
      return winners.map(p => p._wid);
    }

    // For single participant competitive games
    if (participants.length === 1) {
      const participant = participants[0];
      const wid = participantWinnerId(participant, 0);
      if (sessionData.win_loss === "Win") {
        return [wid];
      } else if (sessionData.win_loss === "Loss") {
        return [0];
      }
      if (participant.score && parseFloat(participant.score) > 0) {
        return [wid];
      }
    }

    return [0]; // System dummy user as fallback
  }, [sessionData.participants, sessionData.win_loss, isCoopGame, participantWinnerId]);

  // Initialize winners once on mount (determination source of truth)
  const [winnersInitialized, setWinnersInitialized] = useState(false);

  useEffect(() => {
    if (!winnersInitialized && sessionData.participants.length > 0) {
      const winners = determineWinners();
      setSessionData(prev => ({ ...prev, winners }));
      setWinnersInitialized(true);
    }
  }, [winnersInitialized, sessionData.participants.length, determineWinners]);

  // Manual re-detect winners (user-triggered)
  const handleRedetectWinners = () => {
    const winners = determineWinners();
    setSessionData(prev => ({ ...prev, winners }));
  };

  // Rating modal state (using AddGameModal)
  const [ratingModalGame, setRatingModalGame] = useState(null);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [ratingModalParticipantIndex, setRatingModalParticipantIndex] = useState(-1);

  // Notes section expansion state
  const [showNotes, setShowNotes] = useState(!!initialSessionData?.notes);

  // Color picker dropdown state for each participant
  const [openColorPicker, setOpenColorPicker] = useState(null);

  // Ref to track participants without causing dependency issues
  const participantsRef = useRef(sessionData.participants);
  participantsRef.current = sessionData.participants;

  // Ref to the latest submit handler so effects/deferred callbacks avoid stale closures
  const handleSessionSubmitRef = useRef(null);

  const storageKey = useMemo(() => {
    if (sessionId) return null;
    if (onDataChange) return null;
    const gameId = sessionData.game?.id || sessionData.game?.game_id;
    return user?.id && gameId ? `${STORAGE_PREFIX}:${user.id}:${gameId}` : null;
  }, [sessionData.game, sessionId, onDataChange, user?.id]);

  const storageKeyRef = useRef(storageKey);
  useEffect(() => {
    storageKeyRef.current = storageKey;
  }, [storageKey]);

  const stateRef = useRef({ sessionData, winnersInitialized });
  useEffect(() => {
    stateRef.current = { sessionData, winnersInitialized };
  }, [sessionData, winnersInitialized]);

  const shouldPersistRef = useRef(true);

  const persistState = useCallback(() => {
    if (!storageKeyRef.current) return;
    try {
      localStorage.setItem(storageKeyRef.current, JSON.stringify(stateRef.current));
    } catch (e) {
      logger.error("Failed to persist SessionForm state:", e);
    }
  }, []);

  const clearPersistedState = useCallback(() => {
    if (!storageKeyRef.current) return;
    try {
      localStorage.removeItem(storageKeyRef.current);
    } catch (e) {
      logger.error("Failed to clear persisted SessionForm state:", e);
    }
  }, []);

  // Restore saved state for the current game, if any
  useEffect(() => {
    if (!storageKey) return;
    if (initialSessionData?.participants?.length) return;
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (saved.sessionData) {
        setSessionData(saved.sessionData);
        setWinnersInitialized(saved.winnersInitialized ?? true);
        setShowNotes(!!saved.sessionData.notes);
      }
    } catch (e) {
      logger.error("Failed to load persisted SessionForm state:", e);
    }
  }, [storageKey, initialSessionData]);

  // Persist on unmount unless the session has already been saved
  useEffect(() => () => {
    if (shouldPersistRef.current) {
      persistState();
    }
  }, [persistState]);

  // Rating modal functions
  const openRatingModal = (participantIndex, isPendingSubmit = false) => {
    const participant = sessionData.participants[participantIndex];
    if (!sessionData.game || !participant) return;

    setRatingModalParticipantIndex(participantIndex);
    pendingSessionSubmitRef.current = isPendingSubmit;
    setRatingModalGame({
      ...sessionData.game,
      preference: participant.rating || null,
      ownership_status: participant.ownership_status || null,
      has_played: participant.has_played ?? false
    });
    setShowRatingModal(true);
  };

  const closeRatingModal = () => {
    setShowRatingModal(false);
    setRatingModalGame(null);
    setRatingModalParticipantIndex(-1);
    pendingSessionSubmitRef.current = false;
  };

  const handleRatingModalSave = (savedGame) => {
    const wasPending = pendingSessionSubmitRef.current;
    if (ratingModalParticipantIndex !== -1) {
      // The rating modal always forces has_played=true, so a played-only save
      // resolves to the no_shelf catch-all when no shelf was selected. This
      // ensures the first-session prompt does not fire again.
      const effectiveOwnershipStatus = savedGame.ownership_status || 'no_shelf';
      const updatedParticipants = sessionData.participants.map((p, i) =>
        i === ratingModalParticipantIndex
          ? { ...p, rating: savedGame.preference, ownership_status: effectiveOwnershipStatus, has_played: savedGame.has_played ?? true }
          : p
      );
      // Flush the modal close and participant update synchronously so the ref is
      // updated and the latest submit handler sees the committed ownership_status
      // when retrying (both for the first-session guard and for collection updates).
      flushSync(() => {
        closeRatingModal();
        setSessionData(prev => ({ ...prev, participants: updatedParticipants }));
      });
      participantsRef.current = updatedParticipants;
    } else {
      closeRatingModal();
    }
    if (wasPending) {
      handleSessionSubmitRef.current?.();
    }
  };

  // Optimized user search functionality for participants
  const searchUsers = useCallback(async (query) => {
    if (!query.trim()) {
      setUserSearchResults([]);
      setShowUserDropdown(false);
      return;
    }

    try {
      // Use the new unified user search endpoint
      const res = await httpRequest(`${API_BASE}/auth/users/search?q=${encodeURIComponent(query)}&limit=5`, {
        method: "GET",
        headers: authService.getAuthHeaders()
      });

      if (res.ok) {
        const data = await res.json();
        // Use the standardized response format
        setUserSearchResults(data.users || []);
        setShowUserDropdown(data.users && data.users.length > 0);
      } else {
        setUserSearchResults([]);
        setShowUserDropdown(false);
      }
    } catch (error) {
      logger.error('User search failed:', error);
      setUserSearchResults([]);
    }
  }, []);

  // Function to handle user selection from search results
  const selectUser = async (selectedUser) => {
    setUserSearchQuery(selectedUser.username || selectedUser.name || selectedUser.display_name);
    setShowUserDropdown(false);

    // Add the selected user as a participant
    try {
      // Rating fetch disabled - users set ratings manually via star icon
      // const userRating = await fetchUserRating(sessionData.game?.id, selectedUser.id);

      setSessionData(prev => ({
        ...prev,
        participants: [...prev.participants, {
          user_id: selectedUser.id,
          username: selectedUser.username || selectedUser.name || selectedUser.display_name,
          score: "",
          rating: undefined, // No default rating - user must set via star icon
          color: "", // No default color
          isExisting: true,
          isAutomata: false
        }]
      }));

      setUserSearchQuery("");
      showBanner(`Added existing user: ${selectedUser.username || selectedUser.name || selectedUser.display_name}`, 'success');
    } catch (error) {
      logger.error('Error adding selected user:', error);
      showBanner('Failed to add participant. Please try again.', 'error');
    }
  };

  // Game search functionality
  const searchGames = async (query) => {
    if (!query.trim()) {
      setGameSearchResults([]);
      setShowGameDropdown(false);
      return;
    }

    setGameSearchLoading(true);
    try {
      const res = await httpRequest(`${API_BASE}/games/search?q=${encodeURIComponent(query)}`, {
        method: "GET",
        headers: authService.getAuthHeaders()
      });

      if (query.trim() !== latestGameSearchQueryRef.current.trim()) {
        return;
      }

      if (res.ok) {
        const data = await res.json();
        setGameSearchResults(data.games || []);
        setShowGameDropdown(true);
      } else {
        setGameSearchResults([]);
        setShowGameDropdown(false);
      }
    } catch (error) {
      logger.error('Error searching games:', error);
      setGameSearchResults([]);
      setShowGameDropdown(false);
    } finally {
      if (query.trim() === latestGameSearchQueryRef.current.trim()) {
        setGameSearchLoading(false);
      }
    }
  };

  // Debounced game search
  useEffect(() => {
    latestGameSearchQueryRef.current = gameSearchQuery;
    if (gameSearchQuery) {
      if (skipGameSearchRef.current) {
        skipGameSearchRef.current = false;
        return;
      }

      const timer = setTimeout(() => {
        searchGames(gameSearchQuery);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [gameSearchQuery]);

  // Debounced user search
  useEffect(() => {
    if (userSearchQuery) {
      const timer = setTimeout(() => {
        searchUsers(userSearchQuery);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [userSearchQuery, searchUsers]);

  useEffect(() => {
    const loadGameDetails = async () => {
      const gameId = sessionData.game?.id;
      if (!gameId) {
        setIsCoopGame(false);
        return;
      }

      try {
        const res = await httpRequest(`${API_BASE}/games/${gameId}/details`, {
          method: "GET",
          headers: authService.getAuthHeaders(),
        });

        if (!res.ok) {
          setIsCoopGame(false);
          return;
        }

        const details = await res.json();
        const mechanics = Array.isArray(details?.mechanics) ? details.mechanics : [];
        const categories = Array.isArray(details?.categories) ? details.categories : [];
        const features = [...mechanics, ...categories].map((x) => String(x).toLowerCase());

        const coop = features.some((f) => f.includes("co-operative") || f.includes("cooperative") || f.includes("co-op") || f === "coop");
        setIsCoopGame(coop);

        setSessionData((prev) => {
          if (!coop) {
            if (prev.win_loss === undefined) return prev;
            const { _win_loss, ...rest } = prev;
            return rest;
          }

          return {
            ...prev,
            win_loss: prev.win_loss || "Loss",
          };
        });
      } catch (e) {
        setIsCoopGame(false);
      }
    };

    loadGameDetails();
  }, [sessionData.game?.id]);

  useEffect(() => {
    if (!initialSessionData) return;
    setSessionData((prev) => {
      // Only update participants if this is the initial load (no participants yet)
      // Don't override participants that user has already modified
      const hasUserModifications = prev.participants.length > 0;
      const isInitialLoad = !hasUserModifications;

      return {
        ...prev,
        game: initialGame,
        date: initialSessionData?.date || prev.date,
        chosen_at: initialSessionData?.chosen_at || prev.chosen_at,
        participants: isInitialLoad ? (initialSessionData?.participants || prev.participants) : prev.participants,
        notes: initialSessionData?.notes ?? prev.notes,
        win_loss: initialSessionData?.win_loss ?? prev.win_loss,
        winners: initialSessionData?.winners ?? prev.winners,
      };
    });
  }, [initialSessionData, initialGame]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserDropdown && !event.target.closest('.user-search-container')) {
        setShowUserDropdown(false);
      }
      if (showGameDropdown && !event.target.closest('.game-search-container')) {
        setShowGameDropdown(false);
      }
      if (openColorPicker !== null && !event.target.closest('.color-picker-container')) {
        setOpenColorPicker(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserDropdown, showGameDropdown, openColorPicker]);

  // Rating fetch disabled - users now set ratings manually via star icon modal
  // Fetch current user's rating when game is set (both initial and subsequent changes)
  // useEffect(() => {
  //   const updateCurrentUserRating = async () => {
  //     // Check for both possible ID fields (id or game_id)
  //     const gameId = sessionData.game?.id || sessionData.game?.game_id;
  //
  //     if (gameId && user?.id && participantsRef.current.length > 0) {
  //       const currentUserIndex = participantsRef.current.findIndex(p => p.user_id === user.id);
  //
  //       if (currentUserIndex !== -1) {
  //         // Only update if the current rating is different from the new rating
  //         const currentRating = participantsRef.current[currentUserIndex].rating;
  //         const userRating = await fetchUserRating(gameId, user.id);
  //
  //         if (currentRating !== userRating) {
  //           setSessionData(prev => ({
  //             ...prev,
  //             participants: prev.participants.map((p, index) =>
  //               index === currentUserIndex ? { ...p, rating: userRating } : p
  //             )
  //           }));
  //         }
  //       }
  //     }
  //   };

  //   updateCurrentUserRating();
  // }, [sessionData.game?.id, sessionData.game?.game_id, user?.id, fetchUserRating]);

  // Handle initial game when component mounts (for Edit Session with pre-set game)
  // Rating fetch disabled - edit sessions preserve existing ratings
  // useEffect(() => {
  //   const handleInitialGame = async () => {
  //     if (initialGame?.id && user?.id && participantsRef.current.length > 0) {
  //       const currentUserIndex = participantsRef.current.findIndex(p => p.user_id === user.id);
  //       if (currentUserIndex !== -1) {
  //         const userRating = await fetchUserRating(initialGame.id, user.id);
  //         setSessionData(prev => ({
  //           ...prev,
  //           participants: prev.participants.map((p, index) =>
  //             index === currentUserIndex ? { ...p, rating: userRating } : p
  //           )
  //         }));
  //       }
  //     }
  //   };

  //   handleInitialGame();
  // }, [initialGame?.id, user?.id, fetchUserRating]);

  const updateParticipant = (index, field, value) => {
    setSessionData(prev => ({
      ...prev,
      participants: prev.participants.map((p, i) =>
        i === index ? { ...p, [field]: value } : p
      )
    }));
  };

  const setParticipantAutomata = (index, isAutomata) => {
    setSessionData(prev => ({
      ...prev,
      participants: prev.participants.map((p, i) =>
        i === index ? { ...p, isAutomata } : p
      )
    }));

    if (isAutomata) {
      setPendingInvites(prev => {
        if (!prev[index]) return prev;
        const next = { ...prev };
        delete next[index];
        return next;
      });
    }
  };

  const checkFirstSession = async (gameId) => {
    if (!user?.id || !gameId) return false;
    try {
      const res = await httpRequest(`${API_BASE}/simplify/sessions/check?game_id=${encodeURIComponent(gameId)}`, {
        method: "GET",
        headers: authService.getAuthHeaders()
      });
      if (!res.ok) return false;
      const data = await res.json();
      return data.first_session === true;
    } catch (error) {
      logger.error('Error checking first session:', error);
      return false;
    }
  };

  const removeParticipant = (index) => {
    setSessionData(prev => ({
      ...prev,
      participants: prev.participants.filter((_, i) => i !== index)
    }));
  };

  const addParticipant = async (username = null) => {
    const usernameToAdd = username || newUsername;
    if (!usernameToAdd.trim()) return;

    try {
      // Use the unified user search endpoint to find the user
      const res = await httpRequest(`${API_BASE}/auth/users/search?q=${encodeURIComponent(usernameToAdd.trim())}&limit=1`, {
        method: "GET",
        headers: authService.getAuthHeaders()
      });

      if (res.ok) {
        const data = await res.json();

        // Check if we found an exact match
        const exactMatch = data.users.find(user =>
          (user.username || '').toLowerCase() === usernameToAdd.trim().toLowerCase() ||
          (user.email || '').toLowerCase() === usernameToAdd.trim().toLowerCase()
        );

        if (exactMatch) {
          // Check if user already exists in participants (case-insensitive)
          const alreadyExists = sessionData.participants.some(p =>
            p.user_id === exactMatch.id || p.username.toLowerCase() === (exactMatch.username || '').toLowerCase()
          );

          if (alreadyExists) {
            showBanner('This user is already added to the session', 'error');
            return;
          }

          // Add existing user with no default color
          setSessionData(prev => ({
            ...prev,
            participants: [...prev.participants, {
              user_id: exactMatch.id,
              username: exactMatch.username || exactMatch.name || usernameToAdd.trim(),
              score: "",
              rating: undefined, // No default rating - user must set via star icon
              color: "", // No default color
              isExisting: true,
              isAutomata: false
            }]
          }));

          setNewUsername("");
          showBanner(`Added existing user: ${exactMatch.username || exactMatch.name || usernameToAdd.trim()}`, 'success');
          return;
        }
      }

      // If no existing user found or search failed, treat as new user
      // Check if this new username already exists in participants (case-insensitive)
      const alreadyExists = sessionData.participants.some(p =>
        p.username.toLowerCase() === usernameToAdd.trim().toLowerCase()
      );

      if (alreadyExists) {
        showBanner('This user is already added to the session', 'error');
        return;
      }

      setSessionData(prev => ({
        ...prev,
        participants: [...prev.participants, {
          username: usernameToAdd.trim(),
          score: "",
          rating: undefined, // No default rating - user must set via star icon
          color: "", // No default color
          isExisting: false,
          isAutomata: false
        }]
      }));

      setNewUsername("");
      showBanner(`Added new user: ${usernameToAdd.trim()}`, 'info');

    } catch (error) {
      logger.error('Error adding participant:', error);
      showBanner('Failed to add participant. Please try again.', 'error');
    }
  };

  const handleSessionSubmit = async () => {
    // Prevent duplicate submissions from the same instance
    if (isSubmitting.current) {
      return;
    }
    isSubmitting.current = true;

    if (!sessionData.game) {
      showBanner('Please select a game', 'error');
      finishSubmitting();
      return;
    }

    if (sessionData.participants.length === 0) {
      showBanner('Please add at least one participant', 'error');
      finishSubmitting();
      return;
    }

    if (user?.id && !sessionData.participants.some(p => p.user_id === user.id)) {
      showBanner('You must be a participant to log this session', 'error');
      finishSubmitting();
      return;
    }

    // Ensure we have proper chosen_at timestamp based on selected date (preserve time when editing)
    let chosenAt = sessionData.chosen_at || new Date().toISOString();
    try {
      const existing = new Date(chosenAt);
      const timePart = isNaN(existing.getTime()) ? "12:00:00.000Z" : existing.toISOString().split("T")[1];
      chosenAt = `${sessionData.date}T${timePart}`;
    } catch (e) {
      chosenAt = new Date().toISOString();
    }

    // Ensure we have proper game_id
    const gameId = sessionData.game?.id || sessionData.game?.game_id;

    if (!gameId) {
      showBanner('Invalid game data - missing game ID', 'error');
      finishSubmitting();
      return;
    }

    // For new sessions, if the current user has no rating/ownership for this game
    // and this is their first session for this game, open the rating modal first.
    // Use the ref so pending submits see ownership_status updates made synchronously
    // from the rating modal save.
    const currentParticipants = participantsRef.current;
    if (!sessionId && user?.id) {
      const currentUserIndex = currentParticipants.findIndex(p => p.user_id === user.id);
      const currentUserParticipant = currentUserIndex !== -1 ? currentParticipants[currentUserIndex] : null;
      if (currentUserParticipant && !currentUserParticipant.rating && !currentUserParticipant.ownership_status) {
        const isFirstSession = await checkFirstSession(gameId);
        if (isFirstSession) {
          openRatingModal(currentUserIndex, true);
          finishSubmitting();
          return;
        }
      }
    }

    setIsSaving(true);

    let shareModalOpened = false;

    try {
      // Use manual winner selections if available, otherwise auto-calculate
      const rawWinners = sessionData.winners && sessionData.winners.length > 0
        ? sessionData.winners.map(Number)
        : determineWinners();

      // Resolve any synthetic negative IDs (non-user participants) back to real
      // user_ids for the backend. Non-user winners resolve to 0 (system dummy)
      // since they have no account yet.
      const finalWinners = resolveWinnerIds(rawWinners, sessionData.participants);

      const payload = {
        session_data: {
          participants: sessionData.participants,
          notes: sessionData.notes,
          rating: sessionData.rating === "" ? null : sessionData.rating,
          ...{ win_loss: ((isCoopGame || sessionData.participants.length === 1) ? sessionData.win_loss || "Loss" : null) },
          winners: finalWinners.length > 0 ? finalWinners : [0] // Ensure at least system dummy user
        },
        chosen_at: chosenAt,
        chosen_game_id: gameId
      };

      const url = sessionId ? `${API_BASE}/simplify/session/${sessionId}` : `${API_BASE}/simplify/session`;
      const method = sessionId ? "PUT" : "POST";

      const res = await httpRequest(url, {
        method,
        headers: {
          ...authService.getAuthHeaders(),
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const savedData = await res.json().catch(() => ({}));
        const savedSessionId = savedData.session_id || sessionId;

        // Process pending invites sequentially after the session is saved
        if (savedSessionId && !sessionId) {
          const inviteQueue = sessionData.participants
            .map((p, idx) => ({ p, idx }))
            .filter(({ p, idx }) => !p.isExisting && !p.isAutomata && pendingInvites[idx]);

          if (inviteQueue.length > 0) {
            // Create all invite tokens upfront so every row is in the DB before
            // any modal opens, and onClose can be purely synchronous.
            const resolvedInvites = [];
            for (const { p } of inviteQueue) {
              try {
                const invRes = await httpRequest(`${API_BASE}/invites/session`, {
                  method: "POST",
                  headers: { ...authService.getAuthHeaders(), "Content-Type": "application/json" },
                  body: JSON.stringify({ session_id: savedSessionId, guest_name: p.username })
                });
                if (invRes.ok) {
                  const invData = await invRes.json();
                  resolvedInvites.push({
                    token: invData.token,
                    inviteUrl: invData.invite_url,
                    guestName: invData.guest_name,
                  });
                }
              } catch (err) {
                logger.warn(`Failed to create invite for ${p.username}:`, err);
              }
            }
            if (resolvedInvites.length > 0) {
              const gameName = sessionData.game?.name || '';
              const inviterName = user?.username || user?.name || 'You';
              const stepTotal = resolvedInvites.length;
              const [first, ...rest] = resolvedInvites;
              setActiveShareInvite({ ...first, gameName, inviterName, queue: rest, stepCurrent: 1, stepTotal });
              setShareModalOpen(true);
              shareModalOpened = true;
            } else {
              if (onInvitesDone) onInvitesDone();
            }
          } else {
            if (onInvitesDone) onInvitesDone();
          }
        }

        // Update collections for participants who provided ratings
        const ratingUpdates = [];

        sessionData.participants.forEach(participant => {
          if (participant.rating && participant.rating !== "" && participant.user_id) {
            ratingUpdates.push(
              (async () => {
                // Check current ownership status first
                try {
                  const checkRes = await httpRequest(`${API_BASE}/profile/collection/check`, {
                    method: "POST",
                    headers: {
                      ...authService.getAuthHeaders(),
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                      game_id: gameId,
                      user_id: participant.user_id
                    })
                  });

                  let ownershipStatus = participant.ownership_status || 'no_shelf'; // Default for new ratings

                  if (checkRes.ok) {
                    const currentData = await checkRes.json();
                    // If game is already owned, preserve current ownership status
                    if (currentData.ownership_status &&
                        currentData.ownership_status.toLowerCase() === 'owned') {
                      ownershipStatus = currentData.ownership_status;
                    }
                    // If game is not in collection (ownership_status is null), keep 'no_shelf'
                    // paired with has_played=true below, so new games get logged as played.
                  }

                  return httpRequest(`${API_BASE}/profile/collection`, {
                    method: "POST",
                    headers: {
                      ...authService.getAuthHeaders(),
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                      game_id: gameId,
                      preference: participant.rating,
                      ownership_status: ownershipStatus,
                      has_played: true,
                      user_id: participant.user_id
                    })
                  });
                } catch (error) {
                  logger.error('Error checking ownership status:', error);
                  // Fallback to original behavior
                  return httpRequest(`${API_BASE}/profile/collection`, {
                    method: "POST",
                    headers: {
                      ...authService.getAuthHeaders(),
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                      game_id: gameId,
                      preference: participant.rating,
                      ownership_status: 'no_shelf',
                      has_played: true,
                      user_id: participant.user_id
                    })
                  });
                }
              })()
            );
          }
        });

        if (ratingUpdates.length > 0) {
          try {
            const collectionResults = await Promise.allSettled(ratingUpdates);
            const successfulUpdates = collectionResults.filter(result => result.status === 'fulfilled' && result.value.ok).length;

            if (successfulUpdates === ratingUpdates.length) {
              showBanner(sessionId ? "Session updated and collections updated!" : "Session logged and collections updated!", 'success');
            } else {
              showBanner(sessionId ? "Session updated successfully!" : "Session logged successfully!", 'success');
            }
          } catch (collectionError) {
            showBanner(sessionId ? "Session updated successfully!" : "Session logged successfully!", 'success');
          }
        } else {
          showBanner(sessionId ? "Session updated successfully!" : "Session logged successfully!", 'success');
        }

        shouldPersistRef.current = false;
        clearPersistedState();
        if (onSave) {
          onSave(res);
        }
        setPendingInvites({});
        if (!shareModalOpened) {
          finishSubmitting();
        }
      } else {
        const error = await res.json();
        showBanner(sessionId ? `Failed to update session: ${error.detail || error.message || 'Unknown error'}` : `Failed to log session: ${error.detail || error.message || 'Unknown error'}`, 'error');
        finishSubmitting();
      }
    } catch (error) {
      logger.error('Error logging session:', error);
      showBanner('Failed to log session. Please try again.', 'error');
      finishSubmitting();
    }
  };
  // Keep the ref pointed at the current submit handler so deferred callbacks avoid stale closures
  handleSessionSubmitRef.current = handleSessionSubmit;

  return (
    <div style={{ padding: "clamp(0.75rem, 2vw, 1rem)" }}>
      <h2 style={{ marginBottom: "1rem", color: "var(--text-primary, #333)", fontSize: "clamp(1.1rem, 3vw, 1.25rem)" }}>
        {sessionId ? "Edit Play" : "Log Play"} - {sessionData.game?.name || 'Select a Game'}
      </h2>

      <form onSubmit={(e) => { e.preventDefault(); handleSessionSubmit(); }}>

      {/* Game Selection - Only show if no initial game provided */}
      {!initialGame && (
        <div style={{ marginBottom: "1.5rem" }}>
          <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "bold", fontSize: "clamp(0.875rem, 2vw, 1rem)" }}>
            Game:
          </label>
          <GameSearchDropdown
            searchQuery={gameSearchQuery}
            onSearchChange={(value) => {
              skipGameSearchRef.current = false;
              setGameSearchQuery(value);

              // Only show dropdown if not in the middle of selecting a game
              if (!isSelectingGame) {
                setShowGameDropdown(true);
              }

              // Only update game name if it's a manual search, not from selection
              if (!sessionData.game?.id && !sessionData.game?.game_id) {
                setSessionData(prev => ({ ...prev, game: { name: value } }));
              }
            }}
            isVisible={showGameDropdown && gameSearchQuery}
            onVisibilityChange={(visible) => {
              if (!isSelectingGame) {
                setShowGameDropdown(visible);
              }
            }}
            searchResults={gameSearchResults}
            isLoading={gameSearchLoading}
            onGameSelect={(game) => {
              // Set selection flag to prevent dropdown from showing during state updates
              setIsSelectingGame(true);
              skipGameSearchRef.current = true;

              setSessionData(prev => ({ ...prev, game }));
              setGameSearchQuery(game.name);
              setShowGameDropdown(false);

              // Clear the selection flag after a short delay to allow state updates to complete
              setTimeout(() => {
                setIsSelectingGame(false);
              }, 100);
            }}
            placeholder="Search for a game..."
            showImages={true}
            inputStyle={{
              fontSize: "clamp(0.875rem, 2vw, 1rem)",
              backgroundColor: viewOnly ? "var(--bg-tertiary, #f5f5f5)" : "var(--bg-primary, white)",
              opacity: viewOnly ? 0.6 : 1,
              cursor: viewOnly ? "not-allowed" : "text"
            }}
            dropdownStyle={{
              maxHeight: "200px"
            }}
          />
        </div>
      )}

      {/* Date Selection */}
      <div style={{ marginBottom: "1.5rem" }}>
        <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "bold", fontSize: "clamp(0.875rem, 2vw, 1rem)", color: "var(--text-primary, #333)" }}>
          Date:
        </label>
        <input
          type="date"
          value={sessionData.date}
          onChange={(e) => setSessionData(prev => ({ ...prev, date: e.target.value }))}
          disabled={viewOnly}
          style={{
            width: "100%",
            padding: "0.5rem",
            border: "1px solid var(--border-color, #ddd)",
            borderRadius: "4px",
            fontSize: "clamp(0.875rem, 2vw, 1rem)",
            backgroundColor: viewOnly ? "var(--bg-tertiary, #f5f5f5)" : "var(--bg-secondary, white)",
            color: "var(--text-primary, #333)",
            opacity: viewOnly ? 0.6 : 1,
            cursor: viewOnly ? "not-allowed" : "pointer"
          }}
        />
      </div>


      {/* Participants Section - Moved above Result */}
      <div className="session-form-participants-section" style={{ marginBottom: "1.5rem" }}>
        <div className="session-form-players-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
          <h3 className="session-form-players-label" style={{ margin: 0, color: "var(--text-primary, #333)", fontWeight: "bold" }}>Players</h3>
          {sessionData.participants.length > 0 && !viewOnly && (
            <button
              type="button"
              className="session-form-add-participant-btn"
              onClick={() => setShowAddParticipantInput(!showAddParticipantInput)}
              style={{
                padding: "0.5rem 1rem",
                backgroundColor: "var(--pista-button-bg, #4caf50)",
                color: "white",
                border: "none",
                borderRadius: "50%",
                cursor: "pointer",
                fontSize: "1.5rem",
                width: "40px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                transition: "all 0.2s ease"
              }}
              onMouseOver={(e) => {
                e.target.style.transform = "scale(1.1)";
                e.target.style.boxShadow = "0 4px 12px rgba(0,0,0,0.3)";
              }}
              onMouseOut={(e) => {
                e.target.style.transform = "scale(1)";
                e.target.style.boxShadow = "0 2px 8px rgba(0,0,0,0.2)";
              }}
              title="Add participant"
            >
              +
            </button>
          )}
        </div>

        {/* Participants List */}
        <div className="session-form-participants-list" style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          {sessionData.participants.map((participant, index) => (
            <div key={`${participant.username || 'new'}-${index}-${participant.color || 'no-color'}`} style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              padding: "0.5rem",
              border: "1px solid var(--border-color, #ddd)",
              borderRadius: "4px",
              backgroundColor: "var(--bg-secondary, white)",
              flexWrap: window.innerWidth <= 480 ? "wrap" : "nowrap"
            }}>
              {/* Participant type switch - person or bot */}
              <ParticipantTypeToggle
                isAutomata={participant.isAutomata ?? false}
                onChange={(isAutomata) => setParticipantAutomata(index, isAutomata)}
                disabled={!!(viewOnly && participant.user_id !== user?.id)}
              />

              {/* Username Label with Color Indicator - Click to select color */}
              <div className="color-picker-container" style={{ position: "relative", flex: 1, minWidth: window.innerWidth <= 480 ? "120px" : "150px" }}>
                <div
                  onClick={() => {
                    if (!(viewOnly && participant.user_id !== user?.id)) {
                      setOpenColorPicker(openColorPicker === index ? null : index);
                    }
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    padding: "0.35rem 0.5rem",
                    border: "1px solid var(--border-color, #ddd)",
                    borderRadius: "4px",
                    backgroundColor: (viewOnly && participant.user_id !== user?.id) ? "var(--bg-tertiary, #f5f5f5)" : "var(--bg-primary, white)",
                    cursor: (viewOnly && participant.user_id !== user?.id) ? "not-allowed" : "pointer",
                    opacity: (viewOnly && participant.user_id !== user?.id) ? 0.6 : 1,
                    fontSize: "clamp(0.875rem, 2vw, 1rem)",
                    color: "var(--text-primary, #333)",
                    fontWeight: "500"
                  }}
                  title="Click to change color"
                >
                  {/* Color indicator dot */}
                  <span style={{
                    display: "inline-block",
                    width: "12px",
                    height: "12px",
                    borderRadius: "50%",
                    backgroundColor: participant.color || "#ccc",
                    border: "1px solid var(--border-color, #ddd)",
                    flexShrink: 0
                  }} />
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {participant.username}
                  </span>
                  <span style={{ marginLeft: "auto", fontSize: "0.7rem", color: "var(--text-secondary, #666)" }}>
                    ▼
                  </span>
                </div>

                {/* Color Picker Dropdown */}
                {openColorPicker === index && (
                  <div style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    backgroundColor: "var(--bg-primary, white)",
                    border: "1px solid var(--border-color, #ddd)",
                    borderRadius: "4px",
                    zIndex: 100,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    maxHeight: "150px",
                    overflowY: "auto"
                  }}>
                    {[
                      { value: "#9400D3", label: "🟪 Violet" },
                      { value: "#4B0082", label: "🟦 Indigo" },
                      { value: "#0000FF", label: "🟦 Blue" },
                      { value: "#00FF00", label: "🟩 Green" },
                      { value: "#FFFF00", label: "🟨 Yellow" },
                      { value: "#FFA500", label: "🟧 Orange" },
                      { value: "#FF0000", label: "🟥 Red" },
                      { value: "#000000", label: "⚫ Black" },
                      { value: "#FFFFFF", label: "⚪ White" }
                    ].map((colorOption) => (
                      <div
                        key={colorOption.value}
                        onClick={() => {
                          updateParticipant(index, 'color', colorOption.value);
                          setOpenColorPicker(null);
                        }}
                        style={{
                          padding: "0.5rem",
                          cursor: "pointer",
                          fontSize: "0.875rem",
                          borderBottom: "1px solid var(--border-color, #eee)",
                          backgroundColor: participant.color === colorOption.value ? "var(--bg-tertiary, #e3f2fd)" : "var(--bg-primary, white)"
                        }}
                        onMouseOver={(e) => {
                          if (participant.color !== colorOption.value) {
                            e.target.style.backgroundColor = "var(--bg-tertiary, #f5f5f5)";
                          }
                        }}
                        onMouseOut={(e) => {
                          if (participant.color !== colorOption.value) {
                            e.target.style.backgroundColor = "var(--bg-primary, white)";
                          }
                        }}
                      >
                        {colorOption.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Score */}
              <input
                type="text"
                value={participant.score}
                onChange={(e) => updateParticipant(index, 'score', e.target.value)}
                disabled={viewOnly && participant.user_id !== user?.id}
                placeholder="Score"
                style={{
                  width: window.innerWidth <= 480 ? "60px" : "80px",
                  padding: "0.35rem",
                  border: "1px solid var(--border-color, #ddd)",
                  borderRadius: "4px",
                  fontSize: "clamp(0.875rem, 2vw, 1rem)",
                  backgroundColor: (viewOnly && participant.user_id !== user?.id) ? "var(--bg-tertiary, #f5f5f5)" : "var(--bg-primary, white)",
                  color: "var(--text-primary, #333)",
                  height: "38px",
                  boxSizing: "border-box"
                }}
              />

              {/* Rating - only shown for the logged-in user's own row */}
              {participant.user_id === user?.id && <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  openRatingModal(index);
                }}
                disabled={viewOnly && participant.user_id !== user?.id}
                style={{
                  backgroundColor: "var(--pista-button-bg, #4caf50)",
                  border: participant.rating ? "2px solid var(--pista-button-bg-dark, #2e7d32)" : "2px solid var(--pista-button-bg-light, #81c784)",
                  fontSize: "1.2rem",
                  cursor: (viewOnly && participant.user_id !== user?.id) ? "not-allowed" : "pointer",
                  opacity: (viewOnly && participant.user_id !== user?.id) ? 0.6 : 1,
                  padding: "0.35rem 0.5rem",
                  borderRadius: "6px",
                  transition: "all 0.2s ease",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                  fontWeight: "bold",
                  color: "white",
                  height: "38px",
                  minWidth: window.innerWidth <= 480 ? "50px" : "60px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
                onMouseOver={(e) => {
                  if (!(viewOnly && participant.user_id !== user?.id)) {
                    e.target.style.transform = "scale(1.05)";
                    e.target.style.boxShadow = "0 4px 8px rgba(0,0,0,0.3)";
                  }
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = "scale(1)";
                  e.target.style.boxShadow = "0 2px 4px rgba(0,0,0,0.2)";
                }}
                title={participant.rating ? `Rating: ${participant.rating}` : "Click to set rating"}
              >
                {(participant.rating === 'like' || participant.rating === 'like_a_lot') ? THUMBS_UP_ICON : participant.rating === 'dislike' ? THUMBS_DOWN_ICON : <>{THUMBS_DOWN_ICON}/{THUMBS_UP_ICON}</>}
              </button>}

              {/* Invite Badge - shown for non-user participants when not in viewOnly + not editing + not bot */}
              {!viewOnly && !sessionId && !participant.isExisting && !participant.isAutomata && participant.user_id === undefined && (
                <button
                  type="button"
                  onClick={() => setPendingInvites(prev => ({ ...prev, [index]: !prev[index] }))}
                  title={pendingInvites[index] ? "Cancel invite" : "Invite to Pista"}
                  style={{
                    padding: "0.2rem 0.5rem",
                    fontSize: "0.7rem",
                    fontWeight: "600",
                    border: pendingInvites[index]
                      ? "1px solid var(--pista-button-bg, #4caf50)"
                      : "1px solid var(--border-color, #ddd)",
                    borderRadius: "12px",
                    backgroundColor: pendingInvites[index]
                      ? "var(--pista-button-bg, #4caf50)"
                      : "var(--bg-tertiary, #f5f5f5)",
                    color: pendingInvites[index]
                      ? "var(--pista-button-text, white)"
                      : "var(--text-secondary, #666)",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    height: "28px",
                    display: "flex",
                    alignItems: "center",
                    gap: "3px"
                  }}
                >
                  {pendingInvites[index] ? "✓ Will invite" : "👤 Invite"}
                </button>
              )}

              {/* Remove Button */}
              {!viewOnly && participant.user_id !== user?.id && (
                <button
                  onClick={() => removeParticipant(index)}
                  style={{
                    padding: "0.35rem 0.5rem",
                    backgroundColor: "var(--button-danger-bg, #f44336)",
                    color: "var(--button-danger-text, white)",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "clamp(0.875rem, 2vw, 1rem)",
                    minWidth: window.innerWidth <= 480 ? "40px" : "auto",
                    height: "38px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  ×
                </button>
              )}
            </div>
          ))}

          {/* Add Participant Input - Hidden behind + button */}
          {showAddParticipantInput && !viewOnly && (
            <div className="user-search-container" style={{
              position: "relative",
              marginTop: "0.5rem"
            }}>
              <div style={{
                display: "flex",
                gap: "0.5rem",
                flexDirection: window.innerWidth <= 480 ? "column" : "row"
              }}>
                <input
                  type="text"
                  placeholder="Add participant by username..."
                  value={userSearchQuery}
                  onChange={(e) => {
                    setUserSearchQuery(e.target.value);
                  }}
                  onFocus={() => userSearchQuery && setShowUserDropdown(true)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      // Enter always adds the typed text as a new participant,
                      // ignoring the search dropdown. Selecting an existing
                      // user is only done by clicking a dropdown result.
                      addParticipant(userSearchQuery);
                      setUserSearchQuery("");
                      setShowUserDropdown(false);
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: "0.5rem",
                    border: "1px solid var(--border-color, #ddd)",
                    borderRadius: "4px",
                    fontSize: "clamp(0.875rem, 2vw, 1rem)",
                    backgroundColor: "var(--bg-secondary, white)",
                    color: "var(--text-primary, #333)"
                  }}
                />
              </div>

              {/* User Search Dropdown */}
              {showUserDropdown && userSearchResults.length > 0 && (
                <div style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  backgroundColor: "var(--bg-primary, white)",
                  border: "1px solid var(--border-color, #ddd)",
                  borderRadius: "4px",
                  maxHeight: "200px",
                  overflowY: "auto",
                  zIndex: 1000,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
                }}>
                  {userSearchResults.map((user) => (
                    <div
                      key={user.id}
                      onClick={() => selectUser(user)}
                      style={{
                        padding: "0.75rem",
                        cursor: "pointer",
                        borderBottom: "1px solid var(--border-color, #eee)",
                        backgroundColor: "var(--bg-primary, white)",
                        color: "var(--text-primary, #333)",
                        fontSize: "clamp(0.875rem, 2vw, 1rem)",
                        transition: "background-color 0.2s ease"
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = "var(--bg-tertiary, #f5f5f5)";
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = "var(--bg-primary, white)";
                      }}
                    >
                      <div style={{ fontWeight: "bold" }}>
                        {user.username || user.name || user.display_name}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Result Section - Combined Win/Loss and Winners */}
        <div style={{ marginTop: "1rem", marginBottom: "1rem", padding: "1rem", border: "1px solid var(--border-color, #ddd)", borderRadius: "8px", backgroundColor: "var(--bg-secondary, #f9f9f9)" }}>
          <label style={{ display: "block", fontWeight: "bold", fontSize: "clamp(0.875rem, 2vw, 1rem)", color: "var(--text-primary, #333)", marginBottom: "0.75rem" }}>
            Result
          </label>

          {/* Win/Loss Toggle */}
          {((!isCoopGame && sessionData.participants.length === 1) || isCoopGame) ? (
            <div style={{ marginBottom: "1rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "1rem", backgroundColor: "var(--bg-secondary, white)", padding: "0.75rem", borderRadius: "8px" }}>
                <label className="pill-toggle-switch" style={{
                  position: "relative",
                  display: "inline-block",
                  width: "100px",
                  height: "26px",
                  cursor: viewOnly ? "not-allowed" : "pointer",
                  borderRadius: "13px",
                  backgroundColor: "var(--bg-tertiary, #e9ecef)",
                  border: "1px solid var(--border-color, #ddd)",
                  overflow: "hidden",
                  opacity: viewOnly ? 0.6 : 1
                }}>
                  <input
                    type="checkbox"
                    checked={sessionData.win_loss === "Win"}
                    onChange={(e) => {
                      const newWinLoss = e.target.checked ? "Win" : "Loss";
                      setSessionData((prev) => {
                        // Calculate winners with the new win_loss value
                        const tempState = { ...prev, win_loss: newWinLoss };
                        const winners = (() => {
                          if (!tempState.participants || tempState.participants.length === 0) {
                            return [0]; // System dummy user
                          }

                          // For co-op games
                          if (isCoopGame) {
                            if (newWinLoss === "Win") {
                              return tempState.participants.map((p, idx) => participantWinnerId(p, idx));
                            } else {
                              return [0]; // System dummy user for Loss
                            }
                          }

                          // For competitive games with scores
                          const participantsWithScores = tempState.participants
                            .map((p, idx) => ({ ...p, _wid: participantWinnerId(p, idx) }))
                            .filter(p => p.score && p.score !== "" && !isNaN(parseFloat(p.score)));

                          if (participantsWithScores.length > 0) {
                            const maxScore = Math.max(...participantsWithScores.map(p => parseFloat(p.score)));
                            const ws = participantsWithScores.filter(p => parseFloat(p.score) === maxScore);
                            return ws.map(p => p._wid);
                          }

                          // For single participant competitive games
                          if (tempState.participants.length === 1) {
                            const participant = tempState.participants[0];
                            const wid = participantWinnerId(participant, 0);
                            if (newWinLoss === "Win") {
                              return [wid];
                            } else if (newWinLoss === "Loss") {
                              return [0]; // No winner
                            }
                            if (participant.score && parseFloat(participant.score) > 0) {
                              return [wid];
                            }
                          }

                          return [0]; // System dummy user as fallback
                        })();

                        return { ...prev, win_loss: newWinLoss, winners };
                      });
                    }}
                    disabled={viewOnly}
                    style={{ opacity: 0, width: 0, height: 0, position: "absolute" }}
                  />
                  <div className="toggle-slider" style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "50%",
                    height: "100%",
                    backgroundColor: "var(--pista-button-bg, #4caf50)",
                    transition: "transform 0.3s ease",
                    transform: sessionData.win_loss === "Win" ? "translateX(0)" : "translateX(100%)",
                    borderRadius: "13px",
                    zIndex: 1,
                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                  }}></div>
                  <div className="toggle-options" style={{
                    position: "relative",
                    display: "flex",
                    height: "100%",
                    width: "100%",
                    zIndex: 2,
                    pointerEvents: "auto",
                    background: "transparent"
                  }}>
                    <span className="toggle-option win-option" style={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.65rem",
                      fontWeight: "600",
                      color: sessionData.win_loss === "Win" ? "var(--pista-button-text, white)" : "var(--text-primary, #333)",
                      transition: "color 0.3s ease",
                      userSelect: "none",
                      textShadow: sessionData.win_loss === "Win" ? "0 1px 2px rgba(0,0,0,0.3)" : "none",
                      pointerEvents: "auto"
                    }}>
                      Win
                    </span>
                    <span className="toggle-option loss-option" style={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.65rem",
                      fontWeight: "600",
                      color: sessionData.win_loss === "Loss" ? "var(--pista-button-text, white)" : "var(--text-primary, #333)",
                      transition: "color 0.3s ease",
                      userSelect: "none",
                      textShadow: sessionData.win_loss === "Loss" ? "0 1px 2px rgba(0,0,0,0.3)" : "none",
                      pointerEvents: "auto"
                    }}>
                      Loss
                    </span>
                  </div>
                </label>
              </div>
            </div>
          ) : null}

          {/* Winners Selection Header with Auto-detect */}
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "0.5rem",
            backgroundColor: "var(--bg-secondary, white)",
            padding: "0.75rem",
            borderRadius: "8px"
          }}>
            <label style={{ fontWeight: "600", color: "var(--text-primary, #333)", fontSize: "0.9rem" }}>
              Winners
            </label>
            <button
              type="button"
              onClick={handleRedetectWinners}
              disabled={viewOnly}
              title="Auto-detect winners"
              style={{
                padding: "0.35rem",
                backgroundColor: viewOnly ? "var(--bg-tertiary, #e0e0e0)" : "var(--pista-button-bg, #4caf50)",
                border: "none",
                borderRadius: "4px",
                cursor: viewOnly ? "not-allowed" : "pointer",
                fontSize: "1rem",
                color: viewOnly ? "var(--text-secondary, #999)" : "white",
                opacity: viewOnly ? 0.6 : 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "32px",
                height: "32px"
              }}
            >
              🔄
            </button>
          </div>

          <WinnerSelector
            participants={[
              ...sessionData.participants.map((p, idx) => ({
                id: participantWinnerId(p, idx),
                username: p.username || p.name || 'Unknown'
              })),
              // Add system user (user_id=0) to participants list for winner selection
              { id: 0, username: 'System (No Winner)' }
            ]}
            winners={sessionData.winners}
            setWinners={(newWinners) => {
              logger.debug('SessionForm setWinners called:', newWinners);
              setSessionData(prev => ({ ...prev, winners: newWinners }))
            }}
            disabled={viewOnly}
          />

          {/* No Winners indicator */}
          {sessionData.winners.length === 1 && sessionData.winners[0] === 0 && (
            <p style={{
              margin: "0.5rem 0 0 0",
              fontSize: "0.875rem",
              color: "var(--text-secondary, #666)",
              fontStyle: "italic"
            }}>
              No winners (Loss/No score)
            </p>
          )}
        </div>
      </div>

      {/* Notes Section - Expandable */}
      <div style={{ marginBottom: "1.5rem" }}>
        {!showNotes ? (
          <button
            type="button"
            onClick={() => setShowNotes(true)}
            disabled={viewOnly}
            style={{
              padding: "0.5rem 1rem",
              backgroundColor: viewOnly ? "var(--bg-tertiary, #e0e0e0)" : "var(--pista-button-bg, #4caf50)",
              color: viewOnly ? "var(--text-secondary, #999)" : "white",
              border: "none",
              borderRadius: "4px",
              cursor: viewOnly ? "not-allowed" : "pointer",
              fontSize: "clamp(0.875rem, 2vw, 1rem)",
              fontWeight: "500",
              opacity: viewOnly ? 0.6 : 1
            }}
          >
            + Notes
          </button>
        ) : (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
              <label style={{ fontWeight: "bold", fontSize: "clamp(0.875rem, 2vw, 1rem)", color: "var(--text-primary, #333)" }}>
                Notes:
              </label>
              <button
                type="button"
                onClick={() => setShowNotes(false)}
                style={{
                  padding: "0.25rem 0.5rem",
                  backgroundColor: "transparent",
                  color: "var(--text-secondary, #666)",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "0.875rem"
                }}
              >
                ✕
              </button>
            </div>
            <textarea
              value={sessionData.notes}
              onChange={(e) => setSessionData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Add any notes about this session..."
              rows={3}
              disabled={viewOnly}
              style={{
                width: "100%",
                padding: "0.5rem",
                border: "1px solid var(--border-color, #ddd)",
                borderRadius: "4px",
                resize: "vertical",
                fontSize: "clamp(0.875rem, 2vw, 1rem)",
                backgroundColor: viewOnly ? "var(--bg-tertiary, #f5f5f5)" : "var(--bg-secondary, white)",
                color: "var(--text-primary, #333)",
                opacity: viewOnly ? 0.6 : 1,
                cursor: viewOnly ? "not-allowed" : "text"
              }}
            />
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div style={{
        display: "flex",
        gap: "1rem",
        justifyContent: "flex-end",
        flexDirection: window.innerWidth <= 480 ? "column" : "row"
      }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={isSaving}
          style={{
            padding: "clamp(0.5rem, 1.5vw, 0.75rem) clamp(1rem, 3vw, 1.5rem)",
            backgroundColor: "var(--bg-tertiary, #f5f5f5)",
            color: "var(--text-primary, #333)",
            border: "1px solid var(--border-color, #ddd)",
            borderRadius: "4px",
            cursor: isSaving ? "not-allowed" : "pointer",
            fontSize: "clamp(0.875rem, 2vw, 1rem)",
            width: window.innerWidth <= 480 ? "100%" : "auto",
            opacity: isSaving ? 0.6 : 1
          }}
        >
          Cancel
        </button>
        {viewOnly ? (
          <button
            type="submit"
            disabled={isSaving}
            style={{
              padding: "clamp(0.5rem, 1.5vw, 0.75rem) clamp(1rem, 3vw, 1.5rem)",
              backgroundColor: isSaving ? "#ccc" : "var(--warning-color, #ff9800)",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: isSaving ? "not-allowed" : "pointer",
              fontSize: "clamp(0.875rem, 2vw, 1rem)",
              width: window.innerWidth <= 480 ? "100%" : "auto",
              fontWeight: "bold",
              opacity: isSaving ? 0.7 : 1
            }}
            onMouseOver={(e) => {
              if (isSaving) return;
              e.target.style.backgroundColor = "var(--warning-color-dark, #f57c00)";
            }}
            onMouseOut={(e) => {
              if (isSaving) return;
              e.target.style.backgroundColor = "var(--warning-color, #ff9800)";
            }}
          >
            {isSaving ? "Saving..." : "Save Your Changes"}
          </button>
        ) : (
          <button
            type="submit"
            disabled={isSaving || !sessionData.game || !sessionData.date || sessionData.winners.length === 0}
            style={{
              padding: "clamp(0.5rem, 1.5vw, 0.75rem) clamp(1rem, 3vw, 1.5rem)",
              backgroundColor: (isSaving || !sessionData.game || !sessionData.date || sessionData.winners.length === 0) ? "#ccc" : "var(--pista-button-bg, #1976d2)",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: (isSaving || !sessionData.game || !sessionData.date || sessionData.winners.length === 0) ? "not-allowed" : "pointer",
              fontSize: "clamp(0.875rem, 2vw, 1rem)",
              width: window.innerWidth <= 480 ? "100%" : "auto",
              opacity: isSaving ? 0.7 : 1
            }}
          >
            {isSaving ? "Saving..." : (sessionId ? "Update Session" : "Log Session")}
          </button>
        )}
      </div>
    </form>

      {/* Rating Modal - Using AddGameModal */}
      <AddGameModal
        game={ratingModalGame}
        isOpen={showRatingModal}
        onClose={closeRatingModal}
        onSave={handleRatingModalSave}
        user={user}
        forceHasPlayed={true}
      />

      {/* Session Invite Share Modal */}
      {activeShareInvite && (
        <SessionInviteShareModal
          isOpen={shareModalOpen}
          token={activeShareInvite.token}
          inviteUrl={activeShareInvite.inviteUrl}
          gameName={activeShareInvite.gameName}
          guestName={activeShareInvite.guestName}
          inviterName={activeShareInvite.inviterName}
          stepCurrent={activeShareInvite.stepCurrent}
          stepTotal={activeShareInvite.stepTotal}
          onClose={() => {
            // All tokens were created upfront — onClose is purely synchronous.
            const { queue, gameName, inviterName, stepCurrent, stepTotal } = activeShareInvite;
            if (queue && queue.length > 0) {
              const [next, ...rest] = queue;
              setActiveShareInvite({ ...next, gameName, inviterName, queue: rest, stepCurrent: stepCurrent + 1, stepTotal });
              // keep shareModalOpen true so the modal transitions without blinking
            } else {
              setShareModalOpen(false);
              setActiveShareInvite(null);
              if (onInvitesDone) onInvitesDone();
              finishSubmitting();
            }
          }}
        />
      )}
    </div>
  );
};

export default SessionForm;
